import { useStore } from '@nanostores/react'
import { differenceInMilliseconds, parseISO } from 'date-fns'
import { useState, useTransition } from 'react'
import backgroundImage from './assets/images/paywall-bg.jpeg'
import crownImage from './assets/images/paywall-crown.png'
import { CloseButton } from './components/close-button'
import { DevTools } from './components/dev-tools'
import { Dialog } from './components/dialog'
import { LoadingOverlay } from './components/loading-overlay'
import { SubscribedView } from './components/subscribed-view'
import { useSubscribeMutation } from './hooks/use-subscribe-mutation'
import { m } from './paraglide/messages'
import { native } from '@forest/native-services'
import { splitHighlights } from '@forest/shared'
import { $products } from './store/products'
import { $serviceLevelStatus } from './store/service-level-status'
import type { ServiceLevelStatus } from './types/service-level'

const daysTitle = (days: number) =>
  m.cn_android_paywall_7_day_title({ number: days })

export function App() {
  const serviceLevelStatus = useStore($serviceLevelStatus)

  if (!serviceLevelStatus) return null

  return <AppContent serviceLevelStatus={serviceLevelStatus} />
}

function AppContent({
  serviceLevelStatus,
}: {
  serviceLevelStatus: ServiceLevelStatus
}) {
  const [isSubscribed, setIsSubscribed] = useState(false)
  const { mutateAsync, isPending: isMutationPending } = useSubscribeMutation({
    // Success analytics now fire inside the flow's handleSuccess.
    onSuccess: () => {
      setIsSubscribed(true)
    },
  })
  const [isPending, startTransition] = useTransition()
  const products = useStore($products)
  const isProUser = serviceLevelStatus.is_premium
  const expirationDate = parseISO(serviceLevelStatus.subscription.expires_at)
  const diffMs = differenceInMilliseconds(expirationDate, new Date())
  const distanceInHours = Math.max(0, Math.ceil(diffMs / 3_600_000))
  const distanceInDays = Math.max(0, Math.ceil(diffMs / 86_400_000))
  const isWithinThreeDays = diffMs <= 3 * 86_400_000
  const headingText =
    distanceInHours !== null && distanceInDays !== null
      ? isWithinThreeDays
        ? m.cn_android_paywall_3_day_title({
            countdown_hour: distanceInHours,
          })
        : daysTitle(distanceInDays)
      : daysTitle(7)
  const downgradePoints = isProUser
    ? [
        m.triple_coin_reward(),
        m.plus_exclusive_tree(),
        m.seasonal_crystal_trees(),
        m.app_block(),
      ]
    : [
        m.triple_coin_reward(),
        m.custom_app_allow_list(),
        m.free_use_crystal_tree(),
        m.focus_trend_analysis(),
        m.no_ads(),
      ]

  const handleSubscribe = () => {
    startTransition(async () => {
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'click',
          component_name: 'cna_trial_expiration_paywall_subscribe',
        },
      })

      const product = products?.annualEarlyBird
      if (!product) return

      await mutateAsync(product).catch(() => {})
    })
  }

  return (
    <>
      <div
        className="relative flex min-h-screen w-full flex-col text-white"
        style={{
          background: `linear-gradient(7deg, #7EA540 2.85%, #000 34.1%, #00C1F5 100.93%), url(${backgroundImage}) lightgray 50% / cover no-repeat`,
          backgroundBlendMode: 'screen, normal',
          backdropFilter: 'blur(10px)',
          willChange: 'backdrop-filter',
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_50%_20%,rgba(255,255,255,0.16),rgba(0,0,0,0)),linear-gradient(180deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.18)_100%)]" />

        <div className="relative mx-auto flex max-w-[640px] flex-col items-center px-6 pb-12 pt-[calc(72px+var(--safe-area-top))]">
          <div className="flex flex-col items-center space-y-3 text-center">
            <img
              src={crownImage}
              alt=""
              className="h-[76px] w-[76px] drop-shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
            />
            <h1
              className="whitespace-pre-line text-[26px] font-bold leading-[36px]"
              style={{
                textShadow: '0 0 12px rgba(255, 255, 255, 0.7)',
              }}
            >
              {splitHighlights(headingText).map((part, index) => (
                <span
                  key={index}
                  className={part.highlighted ? 'text-[#2AFD00]' : undefined}
                >
                  {part.text}
                </span>
              ))}
            </h1>
          </div>

          <div className="mt-[60px] flex w-full flex-col items-center space-y-6">
            <p>{m.cn_android_paywall_benefit_lose_title()}</p>

            <div className="flex w-full max-w-[420px] flex-col space-y-3 px-6">
              {downgradePoints.map((text) => (
                <div key={text} className="flex items-center space-x-[10px]">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0"
                  >
                    <path
                      d="M15.7123 7.22699C16.0052 6.93414 16.48 6.93411 16.7729 7.22699C17.0657 7.51988 17.0657 7.99466 16.7729 8.28754L13.06 11.9995L16.7729 15.7123C17.0657 16.0052 17.0658 16.48 16.7729 16.7729C16.48 17.0658 16.0052 17.0657 15.7123 16.7729L11.9995 13.06L8.28754 16.7729C7.99466 17.0657 7.51988 17.0657 7.22699 16.7729C6.93411 16.48 6.93414 16.0052 7.22699 15.7123L10.9389 11.9995L7.22699 8.28754C6.9341 7.99465 6.9341 7.51989 7.22699 7.22699C7.51989 6.9341 7.99465 6.9341 8.28754 7.22699L11.9995 10.9389L15.7123 7.22699Z"
                      fill="#FF5D5D"
                    />
                  </svg>
                  <p className="whitespace-pre-line text-sm">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <p
            className="mt-[60px] whitespace-pre-line text-center text-sm"
            style={{ textShadow: '0 0 12px rgba(255, 255, 255, 0.7)' }}
          >
            {m.cn_android_paywall_benefit_foot_note()}
          </p>
        </div>

        <div className="sticky bottom-0 mx-auto mt-auto flex w-full max-w-[640px] flex-col items-center space-y-4 px-6 pb-12">
          <button
            className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
            onClick={handleSubscribe}
            disabled={isPending || isMutationPending || !products}
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] disabled:cursor-not-allowed disabled:opacity-80 group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {m.cn_android_paywall_benefit_btn()}
            </span>
          </button>
          <button
            className="text-sm font-semibold text-white"
            style={{ textShadow: '0 0 12px rgba(255, 255, 255, 0.7)' }}
            onClick={() => {
              native.general.openURL('forestapp://main_page/subscription_page')
            }}
          >
            {m.other_plan()}
          </button>
        </div>
      </div>

      <CloseButton className="fixed right-4 top-[calc(var(--safe-area-top,0px)+16px)] shadow-[0_0_20px_rgba(0,0,0,0.12)]" />

      <LoadingOverlay isOpen={isPending || isMutationPending} />

      <Dialog />

      {import.meta.env.MODE !== 'production' && <DevTools />}

      <SubscribedView isOpen={isSubscribed} />
    </>
  )
}
